import { baseApi } from '@/Redux/baseApi';
import type { RoyaltyBasis, RoyaltyStatementStatus } from '@/lib/shared-types';
import {
  BRANDS,
  brandAnalyticsPath,
  brandBranchPath,
  brandBranchesPath,
  brandOnboardPath,
  brandPath,
  brandStandardsPath,
  brandTemplatePath,
  brandTemplatesPath,
  royaltySchemePath,
  royaltySchemesPath,
  royaltyStatementsPath,
} from './brandsRoutes';

/**
 * # The whole brands + royalty wire contract, pinned in one place
 *
 * Read directly from `src/modules/brands/` and `src/modules/royalty/` on the
 * backend — `*.controller.ts` for routes and permission keys, `dto/index.ts`
 * for request bodies, `*.service.ts` for what each one actually returns
 * (nothing here carries `@ApiResponse`, so every interface below is read off
 * the service, not off Swagger). Same discipline
 * `Pages/MenuGovernance/menuGovernanceApi.ts` keeps, for the same reason: a
 * late rename on the backend is a one-file fix here instead of a rewrite
 * across five screens.
 *
 * ## Which shape actually got built (ADR-0023)
 *
 * The roadmap's brief asked for a brand/organization hierarchy *above*
 * operational tenants. ADR-0023 rejects that as specified — this schema's
 * tenant isolation is a scalar GUC everywhere it is enforced, and reaching
 * across it means either iterating tenants in application code (forfeiting
 * every SQL aggregate) or switching RLS off — and instead builds a `Brand` as
 * a grouping of branches **inside one tenant**:
 * `TENANT → BRAND → BRANCH → FLOOR → AREA`. Every id below (`brandId`,
 * `branchId`) is scoped to the caller's own tenant; there is no cross-tenant
 * read anywhere in this file, and none of these screens should ever grow one.
 *
 * ## The pagination envelope is NOT `{ data, meta }`
 *
 * `GET /brands` answers `common/dto/pagination.dto.ts`'s `paginate()`
 * verbatim — `{ items, total, page, limit, pages }` — not the `{ data, meta }`
 * shape `Hooks/useApi.ts`'s `rowsOf()` unwraps. See
 * `menuGovernanceApi.ts` for the same note against the same envelope.
 *
 * ## Money
 *
 * Every amount below is the server's own `Decimal` printed as a string.
 * Render every one of them through `useCurrency().format()` — never
 * `parseFloat`, never arithmetic on these fields in a component. A generated
 * statement's `salesAmount`/`royaltyAmount` arrive pre-formatted at the
 * branch's own currency scale (`RoyaltyService.generateStatement` calls
 * `formatMoney`); `listStatements` and `listSchemes` do not — their Decimal
 * columns serialize at their full stored precision (`Decimal(14,4)` /
 * `Decimal(7,4)`), e.g. `"1234.5600"`. `useCurrency().format()` handles both:
 * it rounds display to the currency's own minor unit regardless of how many
 * decimal places the string arrived with, so every screen renders the same
 * figure whichever route it came from.
 */

// --------------------------------------------------------------- pagination

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// -------------------------------------------------------------------- brand

export interface BrandListItem {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  standards: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  _count: { branches: number };
}

export interface BrandBranchRef {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

/** `GET /brands/:brandId` — the row plus every branch it carries. */
export interface BrandDetail extends Omit<BrandListItem, '_count'> {
  branches: BrandBranchRef[];
  _count: { branches: number; royaltySchemes: number };
}

export interface BrandsQuery {
  page?: number;
  limit?: number;
  search?: string;
}

export interface CreateBrandBody {
  name: string;
  slug: string;
  logoUrl?: string;
}

export interface UpdateBrandBody {
  name?: string;
  logoUrl?: string;
}

export interface UpdateBrandStandardsBody {
  standards: Record<string, unknown>;
}

// ------------------------------------------------------------------ analytics

export interface BrandAnalyticsQuery {
  measures: string[];
  dimensions?: string[];
  from?: string;
  to?: string;
}

export type SemanticValue = string | number | null;

/**
 * `BrandsService.analytics` — `{ brandId, branchIds, ...SemanticQueryService.run() }`,
 * except when the brand has no branches at all: that early return skips the
 * semantic engine entirely and so never sets `timezone`. Normalised to
 * `string | null` here rather than left inconsistent across the two paths.
 */
export interface BrandAnalyticsResult {
  brandId: string;
  branchIds: string[];
  measures: string[];
  dimensions: string[];
  dateRange: { from: string | null; to: string | null };
  timezone: string | null;
  rows: Record<string, SemanticValue>[];
  rowCount: number;
  truncated: boolean;
}

// ------------------------------------------------------- onboarding templates

export interface OnboardingTemplate {
  id: string;
  tenantId: string;
  brandId: string;
  name: string;
  isDefault: boolean;
  defaultSettings: Record<string, unknown> | null;
  defaultRoleKeys: string[];
  sourceMenuVersionId: string | null;
  createdAt: string;
}

export interface CreateOnboardingTemplateBody {
  name: string;
  isDefault?: boolean;
  defaultSettings?: Record<string, unknown>;
  defaultRoleKeys?: string[];
  sourceMenuVersionId?: string;
}

export interface OnboardBranchBody {
  name: string;
  code: string;
  templateId?: string;
}

/** `POST /brands/:brandId/onboard` — the new franchisee branch. */
export interface OnboardedBranch {
  id: string;
  tenantId: string;
  brandId: string;
  name: string;
  code: string;
}

// --------------------------------------------------------------------- royalty

export interface RoyaltyScheme {
  id: string;
  tenantId: string;
  brandId: string;
  name: string;
  basis: RoyaltyBasis;
  /** Decimal(7,4) string, e.g. `"5.0000"` — a percentage, 0-100. */
  ratePercent: string;
  /** Decimal(14,4) string, or null. */
  flatFee: string | null;
  /** Decimal(14,4) string, or null. */
  minimumFee: string | null;
  effectiveFrom: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoyaltySchemeBody {
  name: string;
  basis?: RoyaltyBasis;
  ratePercent: string;
  flatFee?: string;
  minimumFee?: string;
  effectiveFrom: string;
}

export interface UpdateRoyaltySchemeBody {
  ratePercent?: string;
  flatFee?: string | null;
  minimumFee?: string | null;
  isActive?: boolean;
}

export interface RoyaltyStatementBranch {
  id: string;
  name: string;
  code: string;
}

/**
 * One generated (or regenerated) royalty period. `basis`, `ratePercent` and
 * `flatFee` are what was actually snapshotted onto THIS statement at
 * generation time — a later edit to the scheme does not reach back and change
 * them (ADR-0023 §7, the same "a rate change cannot re-price the past"
 * principle ADR-0011 §3.1 applies to an order price). That snapshot is the
 * statement's own audit trail: it is what lets a screen show a franchisee
 * exactly what period, basis and rate produced this figure without trusting
 * whatever the scheme says today.
 */
export interface RoyaltyStatement {
  id: string;
  tenantId: string;
  brandId: string;
  branchId: string;
  schemeId: string;
  periodStart: string;
  periodEnd: string;
  basis: RoyaltyBasis;
  /** Decimal(14,4) string — the revenue figure the rate was applied to. */
  salesAmount: string;
  /** Decimal(7,4) string — snapshotted from the scheme at generation time. */
  ratePercent: string;
  /** Decimal(14,4) string, or null — snapshotted from the scheme. */
  flatFee: string | null;
  /** Decimal(14,4) string — the computed royalty owed for this period. */
  royaltyAmount: string;
  currency: string;
  status: RoyaltyStatementStatus;
  generatedById: string;
  generatedAt: string;
  /** Present on `listStatements`; absent on the bare `generateStatement` return. */
  branch?: RoyaltyStatementBranch;
}

export interface GenerateRoyaltyStatementBody {
  branchId: string;
  schemeId: string;
  periodStart: string;
  periodEnd: string;
}

// ------------------------------------------------------------------- endpoints

export const brandsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /brands` — `brands.view`. */
    brands: build.query<Paginated<BrandListItem>, BrandsQuery | void>({
      query: (params) => ({ url: BRANDS, params: params ?? undefined }),
      transformResponse: (response: unknown): Paginated<BrandListItem> => {
        const p = response as Partial<Paginated<BrandListItem>> | null;
        return {
          items: Array.isArray(p?.items) ? (p!.items as BrandListItem[]) : [],
          total: typeof p?.total === 'number' ? p.total : 0,
          page: typeof p?.page === 'number' ? p.page : 1,
          limit: typeof p?.limit === 'number' ? p.limit : 25,
          pages: typeof p?.pages === 'number' ? p.pages : 1,
        };
      },
      providesTags: (result) => [
        { type: 'Brand' as const, id: 'LIST' },
        ...(result?.items ?? []).map((b) => ({ type: 'Brand' as const, id: b.id })),
      ],
    }),

    /** `GET /brands/:brandId` — `brands.view` + `@RequireScope(BRAND, 'brandId')`. */
    brand: build.query<BrandDetail | null, string>({
      query: (brandId) => ({ url: brandPath(brandId) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as BrandDetail) : null,
      providesTags: (_result, _error, brandId) => [{ type: 'Brand' as const, id: brandId }],
    }),

    /** `POST /brands` — `brands.manage`, tenant-admin territory (ADR-0023 §3). */
    createBrand: build.mutation<BrandDetail, CreateBrandBody>({
      query: (body) => ({ url: BRANDS, method: 'post', body }),
      invalidatesTags: [{ type: 'Brand' as const, id: 'LIST' }],
    }),

    /** `PATCH /brands/:brandId` — `brands.manage`. */
    updateBrand: build.mutation<BrandDetail, { brandId: string; body: UpdateBrandBody }>({
      query: ({ brandId, body }) => ({ url: brandPath(brandId), method: 'patch', body }),
      invalidatesTags: (_r, _e, { brandId }) => [
        { type: 'Brand' as const, id: 'LIST' },
        { type: 'Brand' as const, id: brandId },
      ],
    }),

    /** `DELETE /brands/:brandId` — `brands.manage`. Un-groups its branches; does not delete them. */
    removeBrand: build.mutation<void, string>({
      query: (brandId) => ({ url: brandPath(brandId), method: 'delete' }),
      invalidatesTags: [{ type: 'Brand' as const, id: 'LIST' }],
    }),

    /**
     * `PATCH /brands/:brandId/standards` — `brands.manage_standards`,
     * deliberately separate from `brands.manage` so a franchisor can hold
     * this without the authority to redraw brand membership.
     */
    updateBrandStandards: build.mutation<
      BrandDetail,
      { brandId: string; body: UpdateBrandStandardsBody }
    >({
      query: ({ brandId, body }) => ({ url: brandStandardsPath(brandId), method: 'patch', body }),
      invalidatesTags: (_r, _e, { brandId }) => [{ type: 'Brand' as const, id: brandId }],
    }),

    /** `POST /brands/:brandId/branches` — `brands.manage`. */
    assignBranch: build.mutation<unknown, { brandId: string; branchId: string }>({
      query: ({ brandId, branchId }) => ({
        url: brandBranchesPath(brandId),
        method: 'post',
        body: { branchId },
      }),
      invalidatesTags: (_r, _e, { brandId }) => [{ type: 'Brand' as const, id: brandId }],
    }),

    /** `DELETE /brands/:brandId/branches/:branchId` — `brands.manage`. */
    removeBrandBranch: build.mutation<unknown, { brandId: string; branchId: string }>({
      query: ({ brandId, branchId }) => ({
        url: brandBranchPath(brandId, branchId),
        method: 'delete',
      }),
      invalidatesTags: (_r, _e, { brandId }) => [{ type: 'Brand' as const, id: brandId }],
    }),

    /**
     * `POST /brands/:brandId/analytics/query` — `brands.analytics`, distinct
     * from `brands.view`: seeing a sibling franchisee branch's revenue
     * through a brand rollup is a different authority from reading the
     * brand's own standards (ADR-0023 §8).
     */
    brandAnalytics: build.mutation<BrandAnalyticsResult, { brandId: string; body: BrandAnalyticsQuery }>({
      query: ({ brandId, body }) => ({ url: brandAnalyticsPath(brandId), method: 'post', body }),
      transformResponse: (response: unknown, _meta, { brandId }): BrandAnalyticsResult => {
        const r = response as Partial<BrandAnalyticsResult> | null;
        return {
          brandId,
          branchIds: Array.isArray(r?.branchIds) ? (r!.branchIds as string[]) : [],
          measures: Array.isArray(r?.measures) ? (r!.measures as string[]) : [],
          dimensions: Array.isArray(r?.dimensions) ? (r!.dimensions as string[]) : [],
          dateRange: r?.dateRange ?? { from: null, to: null },
          timezone: typeof r?.timezone === 'string' ? r.timezone : null,
          rows: Array.isArray(r?.rows) ? (r!.rows as Record<string, SemanticValue>[]) : [],
          rowCount: typeof r?.rowCount === 'number' ? r.rowCount : 0,
          truncated: Boolean(r?.truncated),
        };
      },
    }),

    /** `GET /brands/:brandId/onboarding-templates` — `brands.view`. */
    onboardingTemplates: build.query<OnboardingTemplate[], string>({
      query: (brandId) => ({ url: brandTemplatesPath(brandId) }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as OnboardingTemplate[]) : [],
      providesTags: (_r, _e, brandId) => [{ type: 'Brand' as const, id: `templates:${brandId}` }],
    }),

    /** `POST /brands/:brandId/onboarding-templates` — `brands.onboard`. */
    createOnboardingTemplate: build.mutation<
      OnboardingTemplate,
      { brandId: string; body: CreateOnboardingTemplateBody }
    >({
      query: ({ brandId, body }) => ({ url: brandTemplatesPath(brandId), method: 'post', body }),
      invalidatesTags: (_r, _e, { brandId }) => [
        { type: 'Brand' as const, id: `templates:${brandId}` },
      ],
    }),

    /** `DELETE /brands/:brandId/onboarding-templates/:templateId` — `brands.onboard`. */
    removeOnboardingTemplate: build.mutation<void, { brandId: string; templateId: string }>({
      query: ({ brandId, templateId }) => ({
        url: brandTemplatePath(brandId, templateId),
        method: 'delete',
      }),
      invalidatesTags: (_r, _e, { brandId }) => [
        { type: 'Brand' as const, id: `templates:${brandId}` },
      ],
    }),

    /**
     * `POST /brands/:brandId/onboard` — `brands.onboard`. Provisions a new
     * franchisee branch. Deliberately does NOT auto-publish a seeded menu —
     * it opens a DRAFT `MenuVersion` that still goes through the ordinary
     * menu-governance review (ADR-0011), which is why this mutation does not
     * invalidate `MenuVersion` tags: nothing menu governance's own screens
     * show has published yet.
     */
    onboardBranch: build.mutation<OnboardedBranch, { brandId: string; body: OnboardBranchBody }>({
      query: ({ brandId, body }) => ({ url: brandOnboardPath(brandId), method: 'post', body }),
      invalidatesTags: (_r, _e, { brandId }) => [{ type: 'Brand' as const, id: brandId }],
    }),

    // ----------------------------------------------------------------- royalty

    /** `GET /brands/:brandId/royalty/schemes` — `royalty.view`. */
    royaltySchemes: build.query<RoyaltyScheme[], string>({
      query: (brandId) => ({ url: royaltySchemesPath(brandId) }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as RoyaltyScheme[]) : [],
      providesTags: (result, _e, brandId) => [
        { type: 'Royalty' as const, id: `schemes:${brandId}` },
        ...(result ?? []).map((s) => ({ type: 'Royalty' as const, id: s.id })),
      ],
    }),

    /** `POST /brands/:brandId/royalty/schemes` — `royalty.configure`. */
    createRoyaltyScheme: build.mutation<
      RoyaltyScheme,
      { brandId: string; body: CreateRoyaltySchemeBody }
    >({
      query: ({ brandId, body }) => ({ url: royaltySchemesPath(brandId), method: 'post', body }),
      invalidatesTags: (_r, _e, { brandId }) => [
        { type: 'Royalty' as const, id: `schemes:${brandId}` },
      ],
    }),

    /** `PATCH /brands/:brandId/royalty/schemes/:schemeId` — `royalty.configure`. */
    updateRoyaltyScheme: build.mutation<
      RoyaltyScheme,
      { brandId: string; schemeId: string; body: UpdateRoyaltySchemeBody }
    >({
      query: ({ brandId, schemeId, body }) => ({
        url: royaltySchemePath(brandId, schemeId),
        method: 'patch',
        body,
      }),
      invalidatesTags: (_r, _e, { brandId, schemeId }) => [
        { type: 'Royalty' as const, id: `schemes:${brandId}` },
        { type: 'Royalty' as const, id: schemeId },
      ],
    }),

    /** `GET /brands/:brandId/royalty/statements` — `royalty.view`. */
    royaltyStatements: build.query<RoyaltyStatement[], string>({
      query: (brandId) => ({ url: royaltyStatementsPath(brandId) }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as RoyaltyStatement[]) : [],
      providesTags: (_r, _e, brandId) => [
        { type: 'Royalty' as const, id: `statements:${brandId}` },
      ],
    }),

    /**
     * `POST /brands/:brandId/royalty/statements` — `royalty.configure`.
     * Generates OR regenerates one `(branch, scheme, period)` — the same
     * unique constraint the backend upserts on, so re-running this for a
     * period already generated updates it in place rather than duplicating
     * it. Outward-facing and hard to reverse (a franchisee reads this as an
     * invoice), so the screen confirms before calling it — see
     * `RoyaltyPanel.tsx`.
     */
    generateRoyaltyStatement: build.mutation<
      RoyaltyStatement,
      { brandId: string; body: GenerateRoyaltyStatementBody }
    >({
      query: ({ brandId, body }) => ({
        url: royaltyStatementsPath(brandId),
        method: 'post',
        body,
      }),
      invalidatesTags: (_r, _e, { brandId }) => [
        { type: 'Royalty' as const, id: `statements:${brandId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useBrandsQuery,
  useBrandQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useRemoveBrandMutation,
  useUpdateBrandStandardsMutation,
  useAssignBranchMutation,
  useRemoveBrandBranchMutation,
  useBrandAnalyticsMutation,
  useOnboardingTemplatesQuery,
  useCreateOnboardingTemplateMutation,
  useRemoveOnboardingTemplateMutation,
  useOnboardBranchMutation,
  useRoyaltySchemesQuery,
  useCreateRoyaltySchemeMutation,
  useUpdateRoyaltySchemeMutation,
  useRoyaltyStatementsQuery,
  useGenerateRoyaltyStatementMutation,
} = brandsApi;
